import { useState, type CSSProperties } from "react";
import type { Item } from "@/lib/types";
import { dueLabel } from "@/lib/format";
import { stripLinks } from "@/lib/links";

/**
 * Todoist subtasks drawn inside their parent: a "1/3 subtasks" toggle, then a
 * compact row per subtask with its own subtasks indented beneath. The board
 * cards, the task lists and the phone all use this one, so a subtask looks the
 * same wherever its parent is.
 *
 * `touch` gives each row a finger-sized target for the phone.
 */
export function SubtaskList({ items, childrenOf, onOpen, onToggle, touch = false, style }: {
  items: Item[];
  childrenOf: (i: Item) => Item[];
  onOpen?: (i: Item) => void;
  onToggle?: (i: Item) => void;
  touch?: boolean;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ borderTop: "1px solid var(--tint2)", paddingTop: 5, marginTop: 6, ...style }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={open ? "Hide subtasks" : "Show subtasks"}
        style={{ ...subToggle, minHeight: touch ? 30 : undefined }}
      >
        <span style={{ display: "inline-block", width: 10, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.12s" }}>›</span>
        <span className="mono">{items.filter((c) => c.status === "done").length}/{items.length}</span>
        {" "}subtask{items.length === 1 ? "" : "s"}
      </button>
      {open && items.map((c) => (
        <SubtaskRow key={c.id} item={c} depth={0} childrenOf={childrenOf} onOpen={onOpen} onToggle={onToggle} touch={touch} />
      ))}
    </div>
  );
}

/**
 * One subtask: a smaller completion circle, the title and its scheduled day,
 * with its own subtasks indented beneath.
 */
function SubtaskRow({ item, depth, childrenOf, onOpen, onToggle, touch }: {
  item: Item; depth: number; childrenOf: (i: Item) => Item[];
  onOpen?: (i: Item) => void; onToggle?: (i: Item) => void; touch: boolean;
}) {
  const done = item.status === "done";
  const due = dueLabel(item.start, "", item.status);
  const kids = depth < 4 ? childrenOf(item) : [];
  const dot = touch ? 17 : 14;
  return (
    <>
      <div
        onClick={() => onOpen?.(item)}
        className="board-subtask"
        style={{
          display: "flex", alignItems: "center", gap: touch ? 9 : 7,
          padding: `${touch ? 7 : 4}px 4px ${touch ? 7 : 4}px ${4 + depth * 16}px`,
          borderRadius: 7, cursor: onOpen ? "pointer" : "default",
        }}
      >
        {onToggle && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(item); }}
            aria-label={done ? "Mark incomplete" : "Mark complete"}
            title={done ? "Mark incomplete" : "Mark complete"}
            style={{
              width: dot, height: dot, flex: "none", borderRadius: "50%",
              border: done ? "none" : "1.5px solid var(--line2)",
              background: done ? "var(--good)" : "transparent",
              color: "var(--onAccent)", cursor: "pointer", fontSize: 9,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
            }}
          >{done ? "✓" : ""}</button>
        )}
        <span
          title={stripLinks(item.title)}
          style={{
            flex: 1, minWidth: 0, fontSize: touch ? 13.5 : 12.5,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: touch ? "normal" : "nowrap",
            textDecoration: done ? "line-through" : "none", color: done ? "var(--muted)" : "var(--ink2)",
          }}
        >{stripLinks(item.title)}</span>
        {due.text && (
          <span style={{ flex: "none", fontSize: 10.5, color: due.overdue ? "var(--bad)" : due.soon ? "var(--warn)" : "var(--muted2)" }}>{due.text}</span>
        )}
      </div>
      {kids.map((k) => (
        <SubtaskRow key={k.id} item={k} depth={depth + 1} childrenOf={childrenOf} onOpen={onOpen} onToggle={onToggle} touch={touch} />
      ))}
    </>
  );
}

/** "↳ Parent title" — over a subtask listed apart from its parent. */
export function ParentHint({ parent }: { parent: Item }) {
  return (
    <div
      title={`Subtask of "${stripLinks(parent.title)}"`}
      style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >↳ {stripLinks(parent.title)}</div>
  );
}

const subToggle: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "none",
  padding: "2px 4px", fontSize: 11, color: "var(--muted)", cursor: "pointer",
};
